import { useEffect, useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import {
  Button,
  Stack,
  SubmitButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import { PencilIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  formatDateDisplayLocal,
  formatTimeDisplayLocal,
  DATE_FORMAT_DISPLAY,
  TIME_FORMAT_DISPLAY,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import _ from 'lodash';

import useReactHookFormProps from '../../hooks/useReactHookFormProps';
import {
  getStartTimeDate,
  isWaitlistStatus,
  showWaitlistUpdateFail,
  showWaitlistUpdateSuccess,
} from '../WaitlistInfoSection/utils';

// eslint-disable-next-line max-len
import { useAsyncApplicationReceived } from '../../containers/ApplicationProfile/LeaseApplicationTab/ApplicationReceived/hooks';
// eslint-disable-next-line max-len
import { validateDateTime } from '../../containers/ApplicationProfile/LeaseApplicationTab/ApplicationReceived/validations';

import useIntl from '../../hooks/useIntl';
import {
  useScreeningStatus,
  useScreeningProviders,
  useValidateApplicants,
  useHouseholdScreening,
  useActiveProviderPropertyProfiles,
  useFinalDecision,
} from '../CreditCriminalStatusScreening/hooks';
import { validateDate } from '../../utils/validations';

import { useOneProperty } from '../../hooks/data-fetching/useOneProperty';

// eslint-disable-next-line max-len
import useAffordableWaitlistValidations from '../../containers/ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';
import useWaitlistService from '../../components/WaitlistInfoSection/hooks';
import { getWaitlistSectionProps, hasCompletedApplicationForm } from './utils';
import componentMessages from './messages';
import appMessages from '../../containers/App/messages';
import { useApplicantForms } from '../HouseholdApplicationsDrawer/hooks';
import { shouldBypassMoveInRequirements } from '../HouseholdApplicationsDrawer/utils';
import { useAppFlags } from '../../utils/featureFlags';

const messages = {
  ...appMessages,
  ...componentMessages,
};

export function useApplicationStatusTracker({
  application,
  householdId,
  providerPropertyProfiles,
  property,
  organizationId,
  propertyId,
  checkUserPermissions,
  isResident,
}) {
  const intl = useIntl();
  const { myfortressAppSignaturePage } = useAppFlags();
  const trackedItems = useMemo(
    () => [
      'Application',
      'Application Fees',
      'Unit Assigned',
      'Screening',
      'Application Status',
    ],
    [],
  );
  const { screeningStatus, finalDecision } = useScreening({
    providerPropertyProfiles,
    householdId,
    application,
    intl,
    isResident,
  });

  const [applicationReceivedDateTime, setApplicationReceivedDateTime] =
    useState(moment(application?.applicationReceived));
  const [applicationReceivedDate, setApplicationReceivedDate] = useState(
    moment(application?.applicationReceived),
  );
  const [applicationReceivedTime, setApplicationReceivedTime] = useState(
    moment(application?.applicationReceived),
  );
  const [applicationReceivedEditMode, setApplicationReceivedEditMode] =
    useState(false);
  const [applicationReceived, updateApplicationReceived, loading] =
    useAsyncApplicationReceived(property, application, promptToaster, intl);

  useEffect(() => {
    if (applicationReceived) {
      setApplicationReceivedDateTime(applicationReceived);
    }
  }, [applicationReceived, loading]);

  useEffect(() => {
    if (
      validateDate(applicationReceivedDate) &&
      validateDate(applicationReceivedTime)
    ) {
      setApplicationReceivedDateTime(
        moment(
          `${formatDateDisplayLocal(
            applicationReceivedDate,
          )} ${formatTimeDisplayLocal(applicationReceivedTime)}`,
          `${DATE_FORMAT_DISPLAY} ${TIME_FORMAT_DISPLAY}`,
        ),
      );
    } else {
      setApplicationReceivedDateTime('');
    }
  }, [applicationReceivedDate, applicationReceivedTime]);

  const handleApplicationReceivedSave = async () => {
    if (!validateDateTime(applicationReceivedDateTime)) {
      if (applicationReceivedEditMode) {
        await updateApplicationReceived(applicationReceivedDateTime);
      }
      setApplicationReceivedEditMode(!applicationReceivedEditMode);
    }
  };

  const { data: formData } = useApplicantForms({
    applicants: application?.applicants,
    propertyId,
    organizationId,
  });

  const bypassMoveInRequirement = shouldBypassMoveInRequirements(property);

  const itemsData = trackedItems.map((item) => {
    switch (item) {
      case 'Application':
        const hasCompletedForm = hasCompletedApplicationForm({
          applicants: application?.applicants,
          formData,
          requireAllAdults: Boolean(myfortressAppSignaturePage),
        });
        // Once the household is a resident the application step is behind them
        // and its form is read-only, so treat it as done the same way the
        // Household Applications drawer does.
        const isComplete =
          isResident || bypassMoveInRequirement || hasCompletedForm;
        return {
          name: item,
          status: isComplete ? 'complete' : 'inProgress',
          data: {
            date: applicationReceivedDate,
            time: applicationReceivedTime,
            editMode: applicationReceivedEditMode,
            setEditMode: setApplicationReceivedEditMode,
            setDate: setApplicationReceivedDate,
            setTime: setApplicationReceivedTime,
            handleSave: handleApplicationReceivedSave,
            loading,
            dateTime: applicationReceivedDateTime,
          },
        };
      case 'Application Fees':
        return {
          name: item,
          status: application?.areFeesPaid ? 'complete' : 'incomplete',
          data: { date: application?.feesPaidAt },
        };
      case 'Unit Assigned':
        return {
          name: item,
          status: application?.au !== null ? 'complete' : 'incomplete',
          data: { date: application?.au?.createdAt },
        };
      case 'Screening':
        return {
          name: item,
          status: screeningStatus,
          data: {
            date: finalDecision?.createdAt,
          },
        };
      case 'Application Status':
        return {
          name: item,
          status: getApplicationStatus(application),
        };
      default:
        return null;
    }
  });

  const propertyDetails = useOneProperty(organizationId, propertyId);
  const { affordableWaitlistFlag } = useAffordableWaitlistValidations();

  const isWaitlistDataReady =
    affordableWaitlistFlag !== undefined &&
    (!propertyDetails?.[0]?.setup ||
      propertyDetails?.[0]?.setup?.affordableWaitlist !== undefined);

  const showWaitlistForm = isWaitlistDataReady
    ? !affordableWaitlistFlag ||
      !propertyDetails?.[0]?.setup?.affordableWaitlist
    : false;

  const [localWaitlistInfo, waitlistActions, isLoading] = useWaitlistService(
    organizationId,
    propertyId,
    application.id,
  );

  const waitlistSectionProps = !showWaitlistForm
    ? {}
    : {
        ...getWaitlistSectionProps(application),
        localWaitlistInfo,
        waitlistActions,
        isLoading,
      };

  const prospectUpdateAllowed = checkUserPermissions(['prospect-update']);
  const applicationReceivedEditAllowed = checkUserPermissions([
    'application-received-edit',
  ]);
  const applicationStatusUpdateAllowed = checkUserPermissions([
    'application-status-update',
    'application-status-update-excluding-approved',
  ]);

  return {
    items: itemsData,
    showWaitlistForm,
    waitlistSectionProps,
    prospectUpdateAllowed,
    applicationReceivedEditAllowed,
    applicationStatusUpdateAllowed,
    localWaitlistInfo,
    waitlistActions,
    isLoading: !isWaitlistDataReady,
  };
}

export function useNotesSection({
  waitlistSectionProps,
  applicationNotes,
  showWaitlistSection,
  handleApplicationNotesChange,
  applicationId,
  prospectUpdateAllowed,
  applicationUpdating,
  isResident,
}) {
  const intl = useIntl();
  const [editMode, setEditMode] = useState(false);
  const [updatingWaitlist, setUpdatingWaitlist] = useState(false);
  const {
    currentApplicationStatus,
    localWaitlistInfo = {},
    waitlistActions,
    isLoading = false,
  } = waitlistSectionProps || {};

  const { waitlistStartTime, waitlistNotes } = localWaitlistInfo;

  const dateTime = useMemo(() => {
    return waitlistStartTime
      ? moment(getStartTimeDate(waitlistStartTime))
      : null;
  }, [waitlistStartTime]);

  const defaultValues = useMemo(
    () => ({
      applicationNotes,
      waitlistNotes,
      waitlistDate: dateTime,
      waitlistTime: dateTime,
    }),
    [applicationNotes, dateTime, waitlistNotes],
  );

  const reactHookFormProps = useReactHookFormProps({
    shouldUnregister: true,
    defaultValues,
  });
  const { isDirty, isValid, reset } = reactHookFormProps;

  const toggleEditMode = useCallback(() => {
    setEditMode((prev) => !prev);
    reset();
  }, [reset]);

  const showWaitlist =
    showWaitlistSection && isWaitlistStatus(currentApplicationStatus);

  const handleSave = useCallback(async () => {
    const dirtyFields = Object.keys(defaultValues).filter((field) => {
      const fieldState = reactHookFormProps.getFieldState(field);
      return fieldState.isDirty;
    });
    const applicationNotesDirty = dirtyFields.includes('applicationNotes');

    if (applicationNotesDirty) {
      const applicationNotes = reactHookFormProps.getValues('applicationNotes');
      handleApplicationNotesChange(applicationNotes.trim());
    }
    const dirtyWaitlistFields = dirtyFields.filter(
      (field) => field !== 'applicationNotes',
    );
    const dateTimeFields = ['waitlistDate', 'waitlistTime'];
    const waitlistSubmitPayload = {};
    dirtyWaitlistFields.forEach((field) => {
      const fieldValue = reactHookFormProps.getValues(field);
      waitlistSubmitPayload[field] = fieldValue;
    });

    const dirtyWaitlistDateTimeFields =
      Object.keys(waitlistSubmitPayload).filter((field) =>
        dateTimeFields.includes(field),
      ).length > 0;

    if (dirtyWaitlistDateTimeFields) {
      const waitlistDate = reactHookFormProps.getValues('waitlistDate');
      waitlistDate.set({
        hour: reactHookFormProps.getValues('waitlistTime').hour(),
        minute: reactHookFormProps.getValues('waitlistTime').minute(),
      });
      delete waitlistSubmitPayload.waitlistDate;
      delete waitlistSubmitPayload.waitlistTime;
      waitlistSubmitPayload.waitlistStartTime = waitlistDate.toISOString();
    }

    if (dirtyWaitlistFields.length > 0) {
      setUpdatingWaitlist(true);
      try {
        await waitlistActions.updateWaitlist(
          applicationId,
          waitlistSubmitPayload,
        );
        showWaitlistUpdateSuccess(intl);
      } catch (error) {
        showWaitlistUpdateFail(intl);
      } finally {
        setUpdatingWaitlist(false);
      }
    }

    toggleEditMode();
  }, [
    applicationId,
    defaultValues,
    handleApplicationNotesChange,
    intl,
    reactHookFormProps,
    toggleEditMode,
    waitlistActions,
  ]);

  const fields = useMemo(
    () => [
      {
        name: 'applicationNotes',
        label: intl.formatMessage(messages.applicationNotes),
        variant: 'text',
        GridProps: {
          xs: 12,
          sm: 6,
        },
        multiline: true,
        minRows: 1,
        disabled: !prospectUpdateAllowed,
        readOnly: !editMode,
      },
      ...(showWaitlist
        ? [
            {
              name: 'waitlistNotes',
              label: intl.formatMessage(messages.waitlistNotes),
              variant: 'text',
              GridProps: {
                xs: 12,
                sm: 6,
              },
              multiline: true,
              minRows: 1,
              readOnly: !editMode,
            },
            {
              variant: 'custom',
              GridProps: {
                xs: 12,
                sm: 6,
              },
            },
            {
              name: 'waitlistDate',
              label: intl.formatMessage(messages.waitlistDateTime),
              variant: 'date',
              GridProps: {
                xs: 3,
                sm: 2,
              },
              multiline: true,
              timezone: 'system',
              disableFuture: true,
              readOnly: !editMode,
            },
            {
              name: 'waitlistTime',
              variant: 'time',
              GridProps: {
                xs: 3,
                sm: 2,
                mt: 3,
              },
              multiline: true,
              timezone: 'system',
              readOnly: !editMode,
            },
          ]
        : []),
      {
        variant: 'custom',
        GridProps: {
          xs: 12,
        },
        component: (
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
            pt={2}
          >
            {editMode && (
              <Button size="medium" variant="text" onClick={toggleEditMode}>
                {intl.formatMessage(messages.cancel)}
              </Button>
            )}
            <SubmitButton
              size="medium"
              variant="primary"
              onClick={handleSave}
              disabled={!isDirty || !isValid}
              isSubmitting={applicationUpdating || updatingWaitlist}
              {...(!editMode && {
                variant: 'text',
                startIcon: <PencilIcon />,
                disabled:
                  isResident ||
                  updatingWaitlist ||
                  applicationUpdating ||
                  (!showWaitlist && !prospectUpdateAllowed),
              })}
            >
              {intl.formatMessage(editMode ? messages.save : messages.edit)}
            </SubmitButton>
          </Stack>
        ),
      },
    ],
    [
      applicationUpdating,
      handleSave,
      isDirty,
      isValid,
      prospectUpdateAllowed,
      showWaitlist,
      toggleEditMode,
      updatingWaitlist,
      intl,
      editMode,
      isResident,
    ],
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, editMode, reset]);

  return {
    isLoading,
    reactHookFormProps,
    fields,
  };
}

function useScreening({
  providerPropertyProfiles,
  householdId,
  application,
  shouldPoll = false,
  intl,
  isResident,
}) {
  const activeProviderPropertyProfiles = useActiveProviderPropertyProfiles(
    providerPropertyProfiles,
  );
  const hasActiveProviderPropertyProfiles =
    activeProviderPropertyProfiles.length > 0;
  const { householdScreening } = useHouseholdScreening({
    householdId,
    shouldPoll,
    enabled: hasActiveProviderPropertyProfiles,
  });

  const { applicantsValidationResult } = useValidateApplicants({
    applicationId: application.id,
    enabled: hasActiveProviderPropertyProfiles && !householdScreening,
  });

  const screeningProviders = useScreeningProviders({
    householdScreening,
    activeProviderPropertyProfiles,
    applicantsValidationResult,
    intl,
  });

  const finalDecision = useFinalDecision({
    householdScreening,
  });
  const screeningStatus = useScreeningStatus({
    screeningProviders,
    applicantsValidationResult,
    finalDecision,
    householdScreening,
    isResident,
  });

  return {
    finalDecision,
    screeningStatus,
  };
}

function promptToaster({ type, title, message }) {
  switch (type) {
    case 'error':
      // Handle error
      toastr.error(title, message);
      break;
    default:
      // Handle success
      toastr.success(title, message);
      break;
  }
}

function getApplicationStatus(application) {
  const status = _.camelCase(application?.applicationStatus?.name);
  switch (status) {
    case 'inProcess': {
      return 'inProgress';
    }
    case 'denied': {
      return 'declined';
    }
    case 'convertedToResident': {
      return 'approved';
    }
    default: {
      return status;
    }
  }
}
